import { useEffect, useRef, useState } from 'react';
import { supabase } from '../lib/supabaseClient';
import { DEFAULT_DATA } from './useDataManagement';

export function useSupabaseSync(user, quadrants, setQuadrants, showToast) {
  const [isInitialLoadDone, setIsInitialLoadDone] = useState(false);
  const skipNextSync = useRef(false);

  // 1. Tải dữ liệu từ Supabase khi đăng nhập
  useEffect(() => {
    if (!user) {
      setIsInitialLoadDone(false);
      return;
    }

    const loadData = async () => {
      try {
        const { data: qData, error: qErr } = await supabase
          .from('quadrants')
          .select('*')
          .eq('user_id', user.id)
          .order('position');

        if (qErr) throw qErr;

        if (!qData || qData.length === 0) {
          // Khởi tạo 4 quadrants cho user mới
          const newQuadrants = [];
          for (const q of DEFAULT_DATA) {
            const { data: insertedQ } = await supabase.from('quadrants').insert({
              user_id: user.id,
              title: q.title,
              color: q.color,
              position: q.id
            }).select().single();
            newQuadrants.push({ ...q, dbId: insertedQ.id });
          }
          skipNextSync.current = true;
          setQuadrants(newQuadrants);
        } else {
          // User cũ: Lấy tasks
          const { data: tData, error: tErr } = await supabase
            .from('tasks')
            .select('*')
            .eq('user_id', user.id)
            .order('order_index');

          if (tErr) throw tErr;

          // Merge dữ liệu vào format của App.js
          const merged = qData.map(q => ({
            id: q.position,
            dbId: q.id,
            title: q.title,
            color: q.color,
            tasks: tData.filter(t => t.quadrant_id === q.id).map(t => ({
              id: t.id,
              text: t.text,
              completed: t.completed,
              order_index: t.order_index
            }))
          }));
          skipNextSync.current = true;
          setQuadrants(merged);
          showToast('Data loaded from cloud');
        }
      } catch (err) {
        console.error('Lỗi khi tải dữ liệu từ Supabase:', err);
        showToast('Error loading from cloud');
      } finally {
        setIsInitialLoadDone(true);
      }
    };

    loadData();
  }, [user, setQuadrants, showToast]);

  // 2. Đồng bộ dữ liệu (Upsert) khi quadrants thay đổi (Debounced)
  useEffect(() => {
    if (!user || !isInitialLoadDone) return;
    
    // Bỏ qua sync nếu state thay đổi do hàm LoadData bên trên
    if (skipNextSync.current) {
      skipNextSync.current = false;
      return;
    }

    const syncData = async () => {
      try {
        // Upsert 4 quadrants
        for (const q of quadrants) {
          if (!q.dbId) continue;
          await supabase.from('quadrants').update({
            title: q.title,
            color: q.color
          }).eq('id', q.dbId);
        }

        // Lấy danh sách tasks hiện có trong DB
        const { data: currentTasks } = await supabase
          .from('tasks')
          .select('id')
          .eq('user_id', user.id);

        const currentTaskIds = currentTasks?.map(t => t.id) || [];
        
        // Chuẩn bị upsert
        const upsertTasks = [];
        const localTaskIds = [];

        quadrants.forEach(q => {
          if (!q.dbId) return;
          q.tasks.forEach((t, index) => {
            localTaskIds.push(t.id);
            upsertTasks.push({
              id: t.id,
              user_id: user.id,
              quadrant_id: q.dbId,
              text: t.text,
              completed: t.completed,
              order_index: index
            });
          });
        });

        if (upsertTasks.length > 0) {
          await supabase.from('tasks').upsert(upsertTasks);
        }

        // Xoá tasks đã bị xoá ở local
        const tasksToDelete = currentTaskIds.filter(id => !localTaskIds.includes(id));
        if (tasksToDelete.length > 0) {
          await supabase.from('tasks').delete().in('id', tasksToDelete);
        }
        
      } catch (err) {
        console.error('Lỗi khi sync data lên Supabase:', err);
      }
    };

    const debounceTimer = setTimeout(() => {
      syncData();
    }, 1000);

    return () => clearTimeout(debounceTimer);
  }, [quadrants, user, isInitialLoadDone]);

  return { isInitialLoadDone };
}
